// FilterBuilder React component for PLDataverseManager
import React, { useState } from "react";
import { OPERATORS, FieldType, FilterCondition, FilterObject } from "../../../shared/filter-types";
import { Input } from "../ui/input";
import { Select } from "../ui/select";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { format, parseISO, isValid } from "date-fns";
import { EntityCombobox } from "../entity-mappings/entity-combobox";

type FieldDef = {
  name: string;
  type: FieldType;
};

type FilterBuilderProps = {
  fields: FieldDef[];
  onChange: (filter: FilterObject) => void;
  initial?: FilterObject;
};

type NullableFilterCondition = Omit<FilterCondition, "value"> & { value: string | number | boolean | null };

type ConditionError = {
  field?: string;
  operator?: string;
  value?: string;
};

function getDefaultOperator(type: FieldType) {
  // Defensive: fallback to "string" if type is invalid
  const ops = OPERATORS[type] ?? OPERATORS["string"];
  return ops[0];
}

function getDefaultValue(type: FieldType) {
  switch (type) {
    case "string":
      return "";
    case "number":
      return 0;
    case "boolean":
      return false;
    case "date":
      return format(new Date(), "yyyy-MM-dd");
    default:
      return "";
  }
}

function validateCondition(
  cond: FilterCondition,
  type: FieldType
): ConditionError | null {
  const errors: ConditionError = {};
  if (!cond.field) errors.field = "Field is required";
  if (!cond.operator) errors.operator = "Operator is required";
  // Do not require value for containsdata/doesnotcontaindata
  if (
    cond.operator !== "containsdata" &&
    cond.operator !== "doesnotcontaindata" &&
    (cond.value === undefined || cond.value === null || cond.value === "")
  ) {
    errors.value = "Value is required";
  } else if (
    cond.operator !== "containsdata" &&
    cond.operator !== "doesnotcontaindata"
  ) {
    if (type === "number" && isNaN(Number(cond.value))) {
      errors.value = "Value must be a number";
    }
    if (type === "date") {
      const valid = isValid(parseISO(String(cond.value)));
      if (!valid) errors.value = "Invalid date";
    }
  }
  return Object.keys(errors).length ? errors : null;
}

export const FilterBuilder: React.FC<FilterBuilderProps> = ({
  fields,
  onChange,
  initial,
}) => {
  const [conditions, setConditions] = useState<NullableFilterCondition[]>(
    initial?.and?.length
      ? initial.and.map(cond => ({ ...cond, value: cond.value ?? null }))
      : (
        fields.length > 0 &&
        fields[0]?.type &&
        OPERATORS[fields[0].type]
      ) ? [
        {
          field: fields[0].name,
          operator: getDefaultOperator(fields[0].type),
          value: getDefaultValue(fields[0].type),
        }
      ] : []
  );
  const [errors, setErrors] = useState<(ConditionError | null)[]>(
    Array(conditions.length).fill(null)
  );

  React.useEffect(() => {
    // Remove nulls before sending to parent
    const filter: FilterObject = { and: conditions.map(({ value, ...rest }) => value === null ? { ...rest, value: null as any } : { ...rest, value }) as any };
    onChange(filter);
    // eslint-disable-next-line
  }, [conditions]);

  const handleFieldChange = (idx: number, fieldName: string) => {
    const field = fields.find((f) => f.name === fieldName);
    if (!field) return;
    const updated = [...conditions];
    updated[idx] = {
      field: field.name,
      operator: getDefaultOperator(field.type),
      value: getDefaultValue(field.type),
    };
    setConditions(updated);
    validateAll(updated);
  };

  const handleOperatorChange = (idx: number, operator: string) => {
    const updated = [...conditions];
    updated[idx].operator = operator;
    setConditions(updated);
    validateAll(updated);
  };

  const handleValueChange = (idx: number, value: any) => {
    const field = fields.find((f) => f.name === conditions[idx].field);
    if (!field) return;
    let parsedValue: any = value;
    if (field.type === "number") parsedValue = Number(value);
    if (field.type === "boolean") parsedValue = Boolean(value);
    const updated = [...conditions];
    updated[idx].value = parsedValue;
    setConditions(updated);
    validateAll(updated);
  };

  const addCondition = () => {
    const firstField = fields[0];
    setConditions([
      ...conditions,
      {
        field: firstField?.name || "",
        operator: firstField ? getDefaultOperator(firstField.type) : "",
        value: getDefaultValue(firstField?.type || "string"),
      },
    ]);
    setErrors([...errors, null]);
  };

  const removeCondition = (idx: number) => {
    const updated = conditions.filter((_, i) => i !== idx);
    setConditions(updated);
    setErrors(errors.filter((_, i) => i !== idx));
  };

  const validateAll = (conds: NullableFilterCondition[]) => {
    const errs = conds.map((cond) => {
      const field = fields.find((f) => f.name === cond.field);
      if (!field) return { field: "Invalid field" };
      return validateCondition(cond as FilterCondition, field.type);
    });
    setErrors(errs);
  };
 
  
  return (
    <Card className="p-4 space-y-4">
      {/* Defensive: Don't render if no fields available */}
      {fields.length === 0 ? (
        <div className="text-muted-foreground italic p-2">No fields available for this entity.</div>
      ) : (
        conditions.map((cond, idx) => {
          const field = fields.find((f) => f.name === cond.field) || fields[0];
          if (!field) {
            return (
              <div key={idx} className="text-red-500 italic p-2">
                Invalid field selection. Please choose a valid field.
              </div>
            );
          }
          const ops = field ? OPERATORS[field.type] : [];
          return (
            <div key={idx} className="flex items-end gap-2">
              {/* Field Combobox */}
              <div className="min-w-[180px]">
                <EntityCombobox
                  entities={fields.map(f => ({
                    LogicalName: f.name,
                    SchemaName: f.name
                  }))}
                  value={cond.field}
                  onChange={(v) => handleFieldChange(idx, v)}
                  isLoading={false}
                  placeholder="Select field"
                  name={`filter-field-${idx}`}
                />
              </div>
              {/* Operator Select */}
              <select
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 justify-between font-normal"
                value={cond.operator}
                onChange={(e) => handleOperatorChange(idx, e.target.value)}
              >
                {(OPERATORS[field.type] || []).map((op: string) => (
                  <option key={op} value={op}>
                    {op}
                  </option>
                ))}
              </select>
              {/* Value Input */}
              {!["containsdata", "doesnotcontaindata"].includes(cond.operator) && field.type === "string" && (
                <Input
                  type="text"
                  value={String(cond.value)}
                  onChange={(e) => handleValueChange(idx, e.target.value)}
                  placeholder="Value"
                />
              )}
              {!["containsdata", "doesnotcontaindata"].includes(cond.operator) && field.type === "number" && (
                <Input
                  type="number"
                  value={String(cond.value)}
                  onChange={(e) => handleValueChange(idx, e.target.value)}
                  placeholder="Value"
                />
              )}
              {!["containsdata", "doesnotcontaindata"].includes(cond.operator) && field.type === "boolean" && (
                <select
                  className="border rounded px-2 py-1"
                  value={String(cond.value)}
                  onChange={(e) => handleValueChange(idx, e.target.value === "true")}
                >
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              )}
              {!["containsdata", "doesnotcontaindata"].includes(cond.operator) && field.type === "date" && (
                <Input
                  type="date"
                  value={String(cond.value)}
                  onChange={(e) => handleValueChange(idx, e.target.value)}
                />
              )}
              {/* Remove Button */}
              {conditions.length > 1 && (
                <Button
                  variant="destructive"
                  onClick={() => removeCondition(idx)}
                  type="button"
                >
                  Remove
                </Button>
              )}
              {/* Validation Error */}
              <div className="text-red-500 text-xs min-w-[120px]">
                {errors[idx] &&
                  Object.values(errors[idx] || {})
                    .filter(Boolean)
                    .join(", ")}
              </div>
            </div>
          );
        })
      )}
      <div>
        <Button type="button" onClick={addCondition}>
          Add Condition
        </Button>
      </div>
    </Card>
  );
};

export default FilterBuilder;